// ResetPassword.jsx
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import logo from "../assets/Product_logo.png";
import { API_URL } from "../config";
import { showNotification } from "../utils/notificationService";

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    // Password validation logic
    const passwordChecks = {
        length: newPassword.length >= 8 && newPassword.length <= 13,
        uppercase: /[A-Z]/.test(newPassword),
        lowercase: /[a-z]/.test(newPassword),
        number: /[0-9]/.test(newPassword),
        special: /[^A-Za-z0-9]/.test(newPassword)
    };

    const handleReset = async () => {
        if (!newPassword || !confirmPassword) {
            showNotification("Please fill in all fields", "error");
            return;
        }
        if (newPassword !== confirmPassword) {
            showNotification("Passwords do not match", "error");
            return;
        }
        if (!Object.values(passwordChecks).every(Boolean)) {
            showNotification("Password does not meet all requirements", "error");
            return;
        }
        if (!token) {
            showNotification("Invalid or missing reset token", "error");
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/auth/reset-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    token,
                    new_password: newPassword,
                    confirm_password: confirmPassword,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || "Failed to reset password");
            }

            setSuccess(true);
        } catch (error) {
            showNotification(error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.wrapper}>
            <div style={styles.card}>
                <img src={logo} alt="Logo" style={styles.logo} />
                <h2 style={styles.heading}>Reset Password</h2>

                {!token ? (
                    <>
                        <p style={styles.errorText}>
                            ⚠️ Invalid or missing reset token. Please request a new password
                            reset link.
                        </p>
                        <Link to="/forgot-password" style={styles.actionBtn}>
                            Request New Link
                        </Link>
                    </>
                ) : success ? (
                    <>
                        <div style={styles.successIcon}>✅</div>
                        <p style={styles.successTitle}>Password Reset!</p>
                        <p style={styles.successText}>
                            Your password has been successfully updated. You can now log in
                            with your new password.
                        </p>
                        <button style={styles.actionBtn} onClick={() => navigate("/")}>
                            Go to Login
                        </button>
                    </>
                ) : (
                    <>
                        <p style={styles.subtitle}>Enter your new password below.</p>

                        {/* New Password */}
                        <div style={styles.passwordContainer}>
                            <input
                                type={showNew ? "text" : "password"}
                                placeholder="New Password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                style={styles.passwordInput}
                            />
                            <button
                                type="button"
                                onClick={() => setShowNew(!showNew)}
                                style={styles.eyeBtn}
                                aria-label="Toggle password visibility"
                            >
                                {showNew ? "🔓" : "🔒"}
                            </button>
                        </div>

                        {/* Password Validation List */}
                        {newPassword.length > 0 && (
                            <div style={{ textAlign: "left", padding: "12px 16px", marginBottom: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                                <div style={passwordChecks.length ? styles.checkPass : styles.checkFail}>
                                    {passwordChecks.length ? "✓" : "✓"} <span style={{ marginLeft: "8px" }}>8-13 Characters</span>
                                </div>
                                <div style={passwordChecks.uppercase ? styles.checkPass : styles.checkFail}>
                                    {passwordChecks.uppercase ? "✓" : "✓"} <span style={{ marginLeft: "8px" }}>Uppercase Letter</span>
                                </div>
                                <div style={passwordChecks.lowercase ? styles.checkPass : styles.checkFail}>
                                    {passwordChecks.lowercase ? "✓" : "✓"} <span style={{ marginLeft: "8px" }}>Lowercase Letter</span>
                                </div>
                                <div style={passwordChecks.number ? microscopicFailOrPass(passwordChecks.number) : styles.checkFail}>
                                    {passwordChecks.number ? "✓" : "✓"} <span style={{ marginLeft: "8px" }}>Number</span>
                                </div>
                                <div style={passwordChecks.special ? styles.checkPass : styles.checkFail}>
                                    {passwordChecks.special ? "✓" : "✓"} <span style={{ marginLeft: "8px" }}>Special Character</span>
                                </div>
                            </div>
                        )}
                        {!passwordChecks.length && newPassword.length > 0 && (
                            <p style={{ color: "#ef4444", fontSize: "12px", textAlign: "left", marginBottom: "16px", marginTop: "-8px" }}>
                                Password is too short ({newPassword.length}/8 characters)
                            </p>
                        )}
                        {/* Confirm Password */}
                        <div style={styles.passwordContainer}>
                            <input
                                type={showConfirm ? "text" : "password"}
                                placeholder="Confirm New Password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleReset()}
                                style={styles.passwordInput}
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirm(!showConfirm)}
                                style={styles.eyeBtn}
                                aria-label="Toggle confirm password visibility"
                            >
                                {showConfirm ? "🔓" : "🔒"}
                            </button>
                        </div>

                        <button
                            style={{
                                ...styles.submitBtn,
                                opacity: loading ? 0.7 : 1,
                                cursor: loading ? "not-allowed" : "pointer",
                            }}
                            onClick={handleReset}
                            disabled={loading}
                        >
                            {loading ? "Resetting..." : "Reset Password"}
                        </button>

                        <p style={styles.backLink}>
                            <Link to="/" style={styles.link}>
                                ← Back to Login
                            </Link>
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

/* ================= STYLES ================= */

const styles = {
    wrapper: {
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 40%, #eef2ff 100%)",
    },
    card: {
        width: 380,
        background: "#fff",
        padding: "32px 28px",
        borderRadius: 16,
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        textAlign: "center",
    },
    logo: {
        width: 100,
        height: 120,
        marginBottom: 1,
    },
    heading: {
        fontSize: 22,
        fontWeight: 600,
        marginBottom: 4,
        marginTop: 12,
    },
    subtitle: {
        fontSize: 14,
        color: "#6b7280",
        marginBottom: 22,
        lineHeight: 1.5,
    },
    passwordContainer: {
        display: "flex",
        alignItems: "center",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        height: 44,
        marginBottom: 16,
        paddingRight: 8,
    },
    passwordInput: {
        flex: 1,
        height: "100%",
        border: "none",
        padding: "0 12px",
        fontSize: 14,
        outline: "none",
        borderRadius: 8,
    },
    eyeBtn: {
        background: "none",
        border: "none",
        cursor: "pointer",
        fontSize: 16,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
    },
    submitBtn: {
        width: "100%",
        height: 44,
        background: "#2563eb",
        color: "#fff",
        border: "none",
        borderRadius: 8,
        fontSize: 15,
        fontWeight: 500,
        cursor: "pointer",
    },
    backLink: {
        marginTop: 16,
        fontSize: 13,
    },
    link: {
        color: "#6b7280",
        textDecoration: "none",
        fontSize: 13,
    },
    // Success state
    successIcon: {
        fontSize: 48,
        marginBottom: 12,
        marginTop: 8,
    },
    successTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: "#111827",
        marginBottom: 8,
    },
    successText: {
        fontSize: 14,
        color: "#6b7280",
        lineHeight: 1.6,
        marginBottom: 24,
    },
    actionBtn: {
        display: "inline-block",
        padding: "10px 24px",
        background: "#2563eb",
        color: "#fff",
        borderRadius: 8,
        textDecoration: "none",
        fontSize: 14,
        fontWeight: 500,
        border: "none",
        cursor: "pointer",
    },
    // Error state
    errorText: {
        fontSize: 14,
        color: "#ef4444",
        lineHeight: 1.6,
        marginBottom: 24,
        marginTop: 8,
    },
    checkFail: {
        color: "#94a3b8",
        fontSize: 13,
        marginBottom: 6,
        display: "flex",
        alignItems: "center"
    },
    checkPass: {
        color: "#059669",
        fontSize: 13,
        marginBottom: 6,
        display: "flex",
        alignItems: "center"
    }
};

function microscopicFailOrPass(pass) {
    return pass ? styles.checkPass : styles.checkFail;
}
